import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import reportService from "../services/report.service";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";
import { formatDateTimeWithZone, formatDateOnly } from "../utils/dateUtils";

const WorkstationIpAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState([]);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(1); // Default: 1 month
  const [sameDay, setSameDay] = useState(true); // Default: true (checked)
  const [sortColumn, setSortColumn] = useState('conflictingDay'); // Default sort by conflicting day
  const [sortDirection, setSortDirection] = useState('desc'); // Default: newest first (descending)

  const { user: currentUser } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!currentUser) return;

    const fetchAnalysis = async () => {
      setLoading(true);
      setError(null);
      try {
        const months = dateRange === 0 ? 0 : dateRange;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const response = await reportService.getWorkstationIpAnalysis(months, sameDay, timezone);
        setAnalysisData(response.data);
      } catch (err) {
        const errorMessage =
          (err.response && err.response.data) ||
          err.message ||
          "Failed to load workstation IP analysis";
        setError(errorMessage);
        EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load workstation IP analysis" });
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysis();
  }, [currentUser, dateRange, sameDay]);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  const handleRowClick = (item) => {
    const { clientId, clientName, conflictingDay } = item;

    // Open workstation IP conflict view in a new tab with conflict details
    let url = `/reports/workstation-ip-conflict-view?clientId=${clientId}${clientName ? `&clientName=${encodeURIComponent(clientName)}` : ''}`;

    if (sameDay && conflictingDay) {
      // Case 1: Same day analysis - pass the specific day
      // Use the displayed date string logic to ensure consistency
      const formattedDate = formatDateOnly(conflictingDay);
      // formattedDate is likely M/D/YYYY or MM/DD/YYYY based on locale
      // We need to parse THIS string to ensure we request exactly what the user sees
      const parts = formattedDate.split('/');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        // Pad start to ensure 2 digits
        const apiDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        url += `&conflictingDay=${apiDate}`;
      } else {
        // Fallback if locale format is unexpected, treat as UTC string to get date part
        const date = new Date(conflictingDay);
        const apiDate = date.toISOString().split('T')[0];
        url += `&conflictingDay=${apiDate}`;
      }
    } else if (!sameDay) {
      // Case 2: Range analysis - pass the number of months
      url += `&months=${dateRange}`;
    }

    window.open(url, "_blank");
  };

  const handleDateRangeChange = (e) => {
    setDateRange(parseInt(e.target.value));
  };

  const handleSameDayChange = (e) => {
    setSameDay(e.target.checked);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedData = () => {
    if (!sortColumn) return analysisData;

    const sorted = [...analysisData].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'clientName') {
        aValue = (a.clientName || '').toLowerCase();
        bValue = (b.clientName || '').toLowerCase();
      } else if (sortColumn === 'conflictingDay') {
        aValue = sameDay ? new Date(a.conflictingDay || 0) : new Date(a.lastLogin || 0);
        bValue = sameDay ? new Date(b.conflictingDay || 0) : new Date(b.lastLogin || 0);
      } else {
        return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <span style={{ opacity: 0.3, marginLeft: '5px' }}>▼</span>;
    }
    return sortDirection === 'asc'
      ? <span style={{ marginLeft: '5px' }}>▲</span>
      : <span style={{ marginLeft: '5px' }}>▼</span>;
  };

  return (
    <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
      <div className="row">
        <div className="col-12">
          <h2>Workstation IP Analysis</h2>
          <p className="text-muted">
            Clients with workstations across multiple IP networks
          </p>
        </div>
      </div>

      <div className="row mt-3 mb-3">
        <div className="col-12 col-md-4">
          <label htmlFor="dateRange" className="form-label">
            Date Range:
          </label>
          <select
            id="dateRange"
            className="form-select"
            value={dateRange}
            onChange={handleDateRangeChange}
          >
            <option value={1}>Last 1 Month</option>
            <option value={3}>Last 3 Months</option>
            <option value={6}>Last 6 Months</option>
            <option value={12}>Last 1 Year</option>
            <option value={0}>All Time</option>
          </select>
        </div>
        <div className="col-12 col-md-4">
          <label className="form-label" style={{ visibility: 'hidden' }}>
            &nbsp;
          </label>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="sameDayCheck"
              checked={sameDay}
              onChange={handleSameDayChange}
            />
            <label className="form-check-label" htmlFor="sameDayCheck">
              Same Day
            </label>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center mt-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="alert alert-danger mt-4" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="row mt-4">
          <div className="col-12">
            {analysisData.length === 0 ? (
              <div className="alert alert-info" role="alert">
                No clients found with workstations across multiple IP networks in the selected date range.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th>Client ID</th>
                      <th
                        onClick={() => handleSort('clientName')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title="Click to sort by Client Name"
                      >
                        Client Name {getSortIcon('clientName')}
                      </th>
                      <th>Workstation Count</th>
                      <th>Network Count</th>
                      <th
                        onClick={() => handleSort('conflictingDay')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title={`Click to sort by ${sameDay ? "Conflicting Day" : "Most Recent Event"}`}
                      >
                        {sameDay ? "Conflicting Day" : "Most Recent Event"} {getSortIcon('conflictingDay')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedData().map((item, index) => (
                      <tr
                        key={item.clientId || index}
                        onClick={() => handleRowClick(item)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{item.clientId || "—"}</td>
                        <td>
                          <strong>{item.clientName || "—"}</strong>
                        </td>
                        <td>{item.workstationCount || 0}</td>
                        <td>
                          <span className="badge bg-warning text-dark">
                            {item.networkCount || 0}
                          </span>
                        </td>
                        <td>{formatDateOnly(sameDay ? item.conflictingDay : item.lastLogin)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkstationIpAnalysis;

